import { Fragment, useMemo, useState, useEffect } from "react";
import { Breadcrumbs } from "../../../AbstractElements";
import { Card, CardBody, CardHeader, Container, Input, Spinner, Row, Col, Button } from "reactstrap";
import { useDataFetch } from "../../Hooks/Use-data-fetch";
import { LogisticsCountry } from "../../Components/logistics/LogisticsCountry";
import { CityList } from "../../Components/logistics/CityList";
import { CityModal } from "../../Components/logistics/CityModal";
import { CityEditModal } from "../../Components/logistics/CityEditModal";
import { toast } from "react-toastify";

export const ConfigLogistics = () => {
  const [selectedCountryId, setSelectedCountryId] = useState("");
  const [editMode, setEditMode] = useState(false);
  const [countryShippingValue, setCountryShippingValue] = useState("");
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [editingCity, setEditingCity] = useState(null);
  const [pagination, setPagination] = useState({ pageIndex: 0, pageSize: 10 });
  const [filterValue, setFilterValue] = useState("");

  const { getData, patchData, invalidateQuery } = useDataFetch();

  const { data: countriesData, isLoading } = getData(["countries-available"], "/product/countries-available/all");
  const countries = countriesData?.data?.countries ?? [];


  const { data, isLoading: isLoadingCities, refetch: refetchCities } = getData(
    ["citiesWithShippingCost", selectedCountryId, pagination],
    "/logistics/cities-with-shipping-cost",
    { enabled: false }, 
    {
      countryId: selectedCountryId || undefined,
      page: pagination.pageIndex + 1,
      limit: pagination.pageSize,
      name: filterValue || undefined,
    }
  );


  const handleSearch = () => {
    if (!selectedCountryId) return;
    refetchCities({
      countryId: selectedCountryId,
      page: pagination.pageIndex + 1,
      limit: pagination.pageSize,
      name: filterValue || undefined,
    });
  };


  const selectedCountry = useMemo(
    () => countries.find((c) => String(c.country_id) === String(selectedCountryId)) ?? null,
    [countries, selectedCountryId]
  );

  useEffect(() => {
    setCountryShippingValue(selectedCountry?.shippingCost ?? "");
    if (selectedCountryId) {
      refetchCities();
    }
  }, [selectedCountry, selectedCountryId, pagination]);

  const { mutateAsync: updateCountryShipping, isPending: updatingCountry } = patchData(
    selectedCountry ? `/logistics/countries/${selectedCountry.id}` : null,
    {},
    {
      onSuccess: () => {
        toast.success("Costo de envío del país actualizado correctamente");
        setEditMode(false);
        invalidateQuery(["countries-available"]);
      },
      onError: (err) => {
        console.error(err);
        toast.error("Error al actualizar costo del país");
      },
    }
  );

  const handleSaveShippingCost = () => {
    if (!selectedCountry) return;
    updateCountryShipping({ shippingCost: Number(countryShippingValue) });
  };


  const handleFilterChange = (value) => setFilterValue(value);
 

  const toggleModal = () => setIsModalOpen(!isModalOpen);
  const toggleEditModal = () => setIsEditModalOpen(!isEditModalOpen);


  const { mutateAsync: updateCitiesShipping } = patchData(
    "/logistics/cities",
    {},
    {
      onSuccess: (response) => {
        if (response?.message === "logistics_multiple_cities_shipping_cost_updated") {
          toast.success(`Se actualizaron ${response.data.modifiedCount} ciudades correctamente`);
          handleSearch();
          toggleModal();
        } else {
          toast.error("No se pudieron actualizar las ciudades");
        }
      },
      onError: (err) => {
        console.error(err);
        toast.error("Error al guardar las ciudades");
      },
    }
  );

  const handleSaveCities = (cityIds, shippingCost) => {
    if (!cityIds.length) {
      toast.error("Selecciona al menos una ciudad");
      return;
    }
    if (!shippingCost) {
      toast.error("Ingresa un costo de envío");
      return;
    }
    updateCitiesShipping({ cityIds, shippingCost });
  };


  const { mutateAsync: editCityShipping } = patchData(
    editingCity ? `/logistics/cities/${editingCity._id}` : null,
    {},
    {
      onSuccess: (response) => {
        if (response?.message === "logistics_city_shipping_cost_updated") {
          toast.success("Ciudad actualizada correctamente");
          handleSearch();
          toggleEditModal();
        } else {
          toast.error("No se pudo actualizar la ciudad");
        }
      },
      onError: (err) => {
        console.error(err);
        toast.error("Error al actualizar la ciudad");
      },
    }
  );

  const handleEditCity = (city) => {
    setEditingCity(city);
    setIsEditModalOpen(true);
  };

  const handleSaveEditCity = (shippingCost) => {
    editCityShipping({ shippingCost });
  };

  return (
    <Fragment>
      <Breadcrumbs mainTitle="Logística" parent="Logística" title="Logística de envíos" />
      <Container fluid>
        <Row>
          <Col md="12">
            <Card className="mb-4 shadow-sm border-0">
              <CardHeader className="d-flex flex-column flex-md-row justify-content-between align-items-md-center">
                <h5 className="mb-2 mb-md-0 fw-bold">Configuración de logística de envíos</h5>
                <div style={{ minWidth: 250 }}>
                  <Input
                    type="select"
                    value={selectedCountryId}
                    onChange={(e) => {
                      setSelectedCountryId(e.target.value);
                      setEditMode(false);
                    }}
                    disabled={isLoading || countries.length === 0}
                  >
                    <option value="">{isLoading ? "Cargando países..." : "Selecciona un país"}</option>
                    {countries.map((country) => (
                      <option key={country.country_id} value={country.country_id}>
                        {country.name}
                      </option>
                    ))}
                  </Input>
                </div>
              </CardHeader>
              <CardBody>
                {isLoading ? (
                  <div className="d-flex align-items-center py-3">
                    <Spinner size="sm" className="me-2" /> Cargando países...
                  </div>
                ) : !selectedCountry ? (
                  <div className="text-center py-5">
                    <h4 className="fw-bold mb-2">Selecciona un país</h4>
                    <p className="text-muted">Para configurar la logística de envíos primero debes escoger un país.</p>
                  </div>
                ) : (
                  <>
                    <Row className="mb-4">
                      <LogisticsCountry
                        selectedCountry={selectedCountry}
                        countryShippingValue={countryShippingValue}
                        setCountryShippingValue={setCountryShippingValue}
                        editMode={editMode}
                        setEditMode={setEditMode}
                        handleSaveShippingCost={handleSaveShippingCost}
                        isPending={updatingCountry}
                        cityCount={data?.data?.total}
                      />
                    </Row>
                    <Row className="mb-3">
                      <Col className="d-flex justify-content-end">
                        <Button color="success" onClick={toggleModal}>Registrar</Button>
                      </Col>
                    </Row>
                    <Row>
                      <Col md="12">
                        <CityList
                          onEdit={handleEditCity}
                          data={data}
                          isLoading={isLoadingCities}
                          pagination={pagination}
                          setPagination={setPagination}
                          filterValue={filterValue}
                          onFilterChange={handleFilterChange}
                          onSearch={handleSearch}
                        />
                      </Col>
                    </Row>
                  </>
                )}
              </CardBody>
            </Card>
          </Col>
        </Row>
      </Container>

      {isModalOpen && (
        <CityModal
          countryId={selectedCountryId}
          isModalOpen={isModalOpen}
          toggleModal={toggleModal}
          handleSaveCities={handleSaveCities}
          currency={selectedCountry?.currency}
        />
      )}

      {isEditModalOpen && editingCity && (
        <CityEditModal
          city={editingCity}
          isModalOpen={isEditModalOpen}
          toggleModal={toggleEditModal}
          handleSaveEdit={handleSaveEditCity}
          currency={selectedCountry?.currency}
        />
      )}
    </Fragment>
  );
};
